// 通用贷款计算引擎（JS，Composition 外的纯函数层）
// 约定：金额单位为元，年化利率以百分比输入（如 4.2 表示 4.2%），期数按月。

// 保留两位小数（现金金额），默认四舍五入
function round2(x) {
  return Math.round((x + Number.EPSILON) * 100) / 100
}

// 等额本息月供（PMT），r 为月利率（小数），n 为月数
function pmtEqualPI(P, r, n) {
  if (n <= 0) return 0
  if (r === 0) return P / n
  const t = Math.pow(1 + r, n)
  return (P * r * t) / (t - 1)
}

// 由 PMT 反推本金（给定 r,n,PMT）
function pvFromPMT(pmt, r, n) {
  if (n <= 0) return 0
  if (r === 0) return pmt * n
  const t = Math.pow(1 + r, n)
  return (pmt * (t - 1)) / (r * t)
}

// 由 PMT 反推期数 n（给定 P,r,pmt）
function nperFromPMT(P, r, pmt) {
  if (r === 0) return P / pmt
  // (pmt - P*r) * (1+r)^n = pmt
  // n = ln(pmt/(pmt - P*r)) / ln(1+r)
  const denom = pmt - P * r
  if (denom <= 0) return Infinity
  return Math.log(pmt / denom) / Math.log(1 + r)
}

// 由 PMT 反推利率 r（给定 P,n,pmt）——使用二分法求解
function rateFromPMT(P, n, pmt, { tol = 1e-10, maxIter = 100, rLow = 0, rHigh = 1 } = {}) {
  // r 范围默认 [0,1] => [0%, 100% 月利率]，足够覆盖一般情况
  let low = rLow
  let high = rHigh
  // f(r) = pmtEqualPI(P, r, n) - pmt，解 f(r)=0
  const f = (r) => pmtEqualPI(P, r, n) - pmt
  let fLow = f(low)
  let fHigh = f(high)
  // 若边界同号，尝试扩展 high
  let expand = 0
  while (fLow * fHigh > 0 && expand < 8) {
    high *= 2
    fHigh = f(high)
    expand++
  }
  for (let i = 0; i < maxIter; i++) {
    const mid = (low + high) / 2
    const fMid = f(mid)
    if (Math.abs(fMid) < tol) return mid
    if (fLow * fMid <= 0) {
      high = mid
      fHigh = fMid
    } else {
      low = mid
      fLow = fMid
    }
  }
  return (low + high) / 2
}

// 生成还款计划表（等额本息）
function scheduleEqualPI({ principal, monthlyRate, months }) {
  const pmt = pmtEqualPI(principal, monthlyRate, months)
  const rows = []
  let remain = principal
  let totalInterest = 0
  for (let i = 1; i <= months; i++) {
    const interest = round2(remain * monthlyRate)
    let principalPay = round2(pmt - interest)
    if (i === months) {
      // 最后一期兜底修正
      principalPay = round2(remain)
    }
    const payment = round2(principalPay + interest)
    remain = round2(remain - principalPay)
    totalInterest = round2(totalInterest + interest)
    rows.push({
      period: i,
      payment,
      principal: principalPay,
      interest,
      balance: Math.max(0, remain)
    })
  }
  return {
    method: 'EPI',
    pmt: round2(pmt),
    totalInterest,
    totalPayment: round2(principal + totalInterest),
    rows
  }
}

// 生成还款计划表（等额本金）
function scheduleEqualP({ principal, monthlyRate, months }) {
  const principalPerMonth = principal / months
  const rows = []
  let remain = principal
  let totalInterest = 0
  for (let i = 1; i <= months; i++) {
    const interest = round2(remain * monthlyRate)
    let principalPay = i === months ? round2(remain) : round2(principalPerMonth)
    const payment = round2(principalPay + interest)
    remain = round2(remain - principalPay)
    totalInterest = round2(totalInterest + interest)
    rows.push({
      period: i,
      payment,
      principal: principalPay,
      interest,
      balance: Math.max(0, remain)
    })
  }
  // 首月与月递减额（常见展示）
  const firstMonthPayment = rows[0]?.payment || 0
  const secondMonthPayment = rows[1]?.payment || firstMonthPayment
  const declinePerMonth = round2(firstMonthPayment - secondMonthPayment)
  return {
    method: 'EP',
    firstMonthPayment,
    declinePerMonth,
    totalInterest,
    totalPayment: round2(principal + totalInterest),
    rows
  }
}

// 统一入口：生成计划表
function generateSchedule({ amount, annualRatePct, months, method = 'EPI' }) {
  const principal = Number(amount)
  const monthlyRate = Number(annualRatePct) / 12 / 100
  const n = Number(months)
  if (!(principal > 0 && n > 0) || !(monthlyRate >= 0)) {
    throw new Error('参数不合法')
  }
  if (method === 'EPI') {
    return scheduleEqualPI({ principal, monthlyRate, months: n })
  }
  if (method === 'EP') {
    return scheduleEqualP({ principal, monthlyRate, months: n })
  }
  throw new Error('未知的还款方式')
}

// 提前还款（一次性部分本金）——两种策略：缩短期数 reduce-term / 降月供 reduce-payment
function applyLumpSumPrepayment(schedule, { monthIndex, amount, strategy = 'reduce-term', baseMonthlyRate } = {}) {
  // schedule 为 generateSchedule 的返回
  const rows = schedule.rows
  if (!rows?.length) return schedule
  const idx = Math.min(Math.max(1, monthIndex), rows.length) // 1-based
  const prepay = Math.max(0, Number(amount) || 0)
  if (prepay <= 0) return schedule

  // 计算该月之前的余额
  let remain = rows[idx - 1].balance
  // 扣减提前还款额
  remain = round2(Math.max(0, remain - prepay))

  const rBase = typeof baseMonthlyRate === 'number' ? baseMonthlyRate : (rows.length > 1 ? (rows[1].interest / (rows[0].balance || 1)) : 0)

  if (strategy === 'reduce-term') {
    // 保持月供（若为等额本息），重新计算剩余期数
    if (schedule.method === 'EPI') {
      const r = rBase
      const pmt = schedule.pmt
      const nLeft = Math.ceil(nperFromPMT(remain, r, pmt))
      const rebuilt = scheduleEqualPI({ principal: remain, monthlyRate: r, months: nLeft })
      // 拼接：已还部分 + 新计划
      const done = rows.slice(0, idx)
      const merged = done.concat(
        rebuilt.rows.map((r0, i) => ({ ...r0, period: idx + i }))
      )
      const totalInterest = round2(done.reduce((s, r) => s + r.interest, 0) + rebuilt.totalInterest)
      return {
        method: 'EPI',
        pmt: rebuilt.pmt,
        totalInterest,
        totalPayment: round2(done[done.length - 1].payment + prepay + (rebuilt.totalPayment - remain)),
        rows: merged
      }
    } else {
      // 等额本金：保持每月本金不变，缩短期数
      const r = rBase
      // 每月本金
      const principalPerMonth = rows[0].principal
      const nLeft = Math.ceil(remain / principalPerMonth)
      const rebuilt = scheduleEqualP({ principal: remain, monthlyRate: r, months: nLeft })
      const done = rows.slice(0, idx)
      const merged = done.concat(
        rebuilt.rows.map((r0, i) => ({ ...r0, period: idx + i }))
      )
      const totalInterest = round2(done.reduce((s, r) => s + r.interest, 0) + rebuilt.totalInterest)
      return {
        method: 'EP',
        firstMonthPayment: rebuilt.firstMonthPayment,
        declinePerMonth: rebuilt.declinePerMonth,
        totalInterest,
        totalPayment: round2(done.reduce((s, r) => s + r.payment, 0) + prepay + rebuilt.totalPayment),
        rows: merged
      }
    }
  }

  if (strategy === 'reduce-payment') {
    // 保持剩余期数，重算月供
  const r = rBase
    const remainMonths = rows.length - idx + 1
    if (schedule.method === 'EPI') {
      const rebuilt = scheduleEqualPI({ principal: remain, monthlyRate: r, months: remainMonths })
      const done = rows.slice(0, idx)
      const merged = done.concat(
        rebuilt.rows.map((r0, i) => ({ ...r0, period: idx + i }))
      )
      const totalInterest = round2(done.reduce((s, r) => s + r.interest, 0) + rebuilt.totalInterest)
      return {
        method: 'EPI',
        pmt: rebuilt.pmt,
        totalInterest,
        totalPayment: round2(done.reduce((s, r) => s + r.payment, 0) + prepay + rebuilt.totalPayment),
        rows: merged
      }
    } else {
      const rebuilt = scheduleEqualP({ principal: remain, monthlyRate: r, months: remainMonths })
      const done = rows.slice(0, idx)
      const merged = done.concat(
        rebuilt.rows.map((r0, i) => ({ ...r0, period: idx + i }))
      )
      const totalInterest = round2(done.reduce((s, r) => s + r.interest, 0) + rebuilt.totalInterest)
      return {
        method: 'EP',
        firstMonthPayment: rebuilt.firstMonthPayment,
        declinePerMonth: rebuilt.declinePerMonth,
        totalInterest,
        totalPayment: round2(done.reduce((s, r) => s + r.payment, 0) + prepay + rebuilt.totalPayment),
        rows: merged
      }
    }
  }

  return schedule
}

export {
  round2,
  pmtEqualPI,
  pvFromPMT,
  nperFromPMT,
  rateFromPMT,
  scheduleEqualPI,
  scheduleEqualP,
  generateSchedule,
  applyLumpSumPrepayment
}
